/**
 * Add/Edit user modal for the gamemaster Users page.
 *
 * In "edit" mode (a `user` prop is supplied) it prefills the form and dispatches
 * `updateUser`; otherwise it creates a pending user (invite-email flow) via
 * `createUser`. Assignable roles are supplied by the parent via `roleOptions`
 * so the same modal can restrict roles per context.
 */

import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Modal, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { RootState, AppDispatch } from '../../store';
import { createUser, updateUser, clearUsersError } from '../../store/usersSlice';
import { User, UserRole } from '../../types/auth';

interface UserFormModalProps {
    show: boolean;
    onHide: () => void;
    /** Supplied => edit mode; omitted/null => add mode. */
    user?: User | null;
    /** Roles the caller is allowed to assign in this context. */
    roleOptions: UserRole[];
}

const UserFormModal: React.FC<UserFormModalProps> = ({
    show,
    onHide,
    user,
    roleOptions,
}) => {
    const isEdit = !!user;
    const dispatch = useDispatch<AppDispatch>();
    const error = useSelector((state: RootState) => state.users.error);

    const [username, setUsername] = useState('');
    const [email, setEmail] = useState('');
    const [role, setRole] = useState<UserRole>(roleOptions[0] ?? 'client');
    const [validated, setValidated] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    // Sync the form with the user being edited whenever the modal opens.
    useEffect(() => {
        if (show) {
            setUsername(user?.username ?? '');
            setEmail(user?.email ?? '');
            setRole(user?.role ?? roleOptions[0] ?? 'client');
            setValidated(false);
            dispatch(clearUsersError());
        }
    }, [show, user, roleOptions, dispatch]);

    const handleClose = () => {
        dispatch(clearUsersError());
        onHide();
    };

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const form = event.currentTarget;
        if (form.checkValidity() === false) {
            event.stopPropagation();
            setValidated(true);
            return;
        }
        setValidated(true);
        setSubmitting(true);
        try {
            if (isEdit && user) {
                await dispatch(
                    updateUser({
                        userId: user.id,
                        data: {
                            username: username.trim(),
                            email: email.trim(),
                            role,
                        },
                    })
                ).unwrap();
            } else {
                await dispatch(
                    createUser({
                        username: username.trim(),
                        email: email.trim(),
                        role,
                    })
                ).unwrap();
            }
            handleClose();
        } catch {
            // Error surfaced via the redux `error` state and the Alert below.
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal show={show} onHide={handleClose} centered>
            <Modal.Header closeButton>
                <Modal.Title>{isEdit ? 'Edit User' : 'Add User'}</Modal.Title>
            </Modal.Header>
            <Form noValidate validated={validated} onSubmit={handleSubmit}>
                <Modal.Body>
                    {error && (
                        <Alert
                            variant="danger"
                            dismissible
                            onClose={() => dispatch(clearUsersError())}
                        >
                            {error}
                        </Alert>
                    )}

                    <Form.Group className="mb-3" controlId="userUsername">
                        <Form.Label>Username</Form.Label>
                        <Form.Control
                            type="text"
                            placeholder="Enter username"
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                            required
                            minLength={1}
                            maxLength={32}
                            disabled={submitting}
                            autoFocus
                        />
                        <Form.Control.Feedback type="invalid">
                            Please enter a username (1-32 characters).
                        </Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group className="mb-3" controlId="userEmail">
                        <Form.Label>Email</Form.Label>
                        <Form.Control
                            type="email"
                            placeholder="Enter email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                            disabled={submitting}
                        />
                        <Form.Control.Feedback type="invalid">
                            Please enter a valid email address.
                        </Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group className="mb-3" controlId="userRole">
                        <Form.Label>Role</Form.Label>
                        <Form.Select
                            value={role}
                            onChange={(e) => setRole(e.target.value as UserRole)}
                            disabled={submitting}
                        >
                            {roleOptions.map((r) => (
                                <option key={r} value={r}>
                                    {r.charAt(0).toUpperCase() + r.slice(1)}
                                </option>
                            ))}
                        </Form.Select>
                    </Form.Group>

                    {!isEdit && (
                        <Alert variant="info" className="mb-0">
                            The user will receive an email with a time-limited link to set
                            their own password. They can&apos;t sign in until they do.
                        </Alert>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleClose} disabled={submitting}>
                        Cancel
                    </Button>
                    <Button variant="primary" type="submit" disabled={submitting}>
                        {submitting ? (
                            <>
                                <Spinner
                                    as="span"
                                    animation="border"
                                    size="sm"
                                    role="status"
                                    aria-hidden="true"
                                    className="me-2"
                                />
                                {isEdit ? 'Saving...' : 'Sending invite...'}
                            </>
                        ) : isEdit ? (
                            'Save Changes'
                        ) : (
                            'Create & Send Invite'
                        )}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};

export default UserFormModal;
